import axios from '@/plugins/axios';
import enumerateArray from '@/helpers/enumerateArray';
import downloadFile from '@/helpers/downloadFile';

const initialState = {
  list: [],
  comission: null,
  expanseTotal: 0,
  configuratorList: [],
  expenseTypesList: [],
  categoriesList: [],
  citiesList: [],
  history: {
    currentPage: 1,
    lastPage: 1,
    perPage: 100,
    list: [],
  },
};

const actions = {
  async fetchList({ commit }, params) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('finance', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      const list = enumerateArray(data.data.data);

      commit('setList', list);
      commit('setTotals', {
        expanseTotal: data.data.total_expanse,
      });

      return data;
    } catch (error) {
      return error;
    }
  },

  async addNew(_, params) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.post('finance', params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async deleteItem(_, id) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.delete(`finance/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async restoreItem(_, id) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.get(`finance/restore/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async updateItem(_, { id, params }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      await axios.put(`finance/${id}`, params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  },

  async fetchExpenseTypes({ commit }, title = '') {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('finance/properties/autocomplete', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          type: 'types',
          title,
        },
      });

      commit('setExpenseTypesList', data.data.result);
      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchCategories({ commit }, { title = '', typeId }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('finance/properties/autocomplete', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          type: 'categories',
          title,
          typeId,
        },
      });

      commit('setCategoriesList', data.data.result);
      return data;
    } catch (error) {
      return error;
    }
  },

  async updateProperty({ commit, state }, { params, type = 'add' }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const method = type === 'add' ? 'post' : 'put';

      const { data } = await axios[method]('finance/properties', params, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      if (!params.typeId) {
        commit('setExpenseTypesList', [
          ...state.expenseTypesList,
          { title: params.title, id: data.data.id },
        ]);
      } else {
        commit('setCategoriesList', [
          ...state.categoriesList,
          { title: params.title, id: data.data.id },
        ]);
      }

      return { success: true, id: data.data.id };
    } catch (error) {
      const message = error.response.data.error.message || error.message;
      return {
        error: true,
        message,
      };
    }
  },

  async fetchConfiguratorList({ commit }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('finance/properties/type_category', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const list = enumerateArray(data.data);

      commit('setConfiguratorList', list);
      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchCities({ commit }) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const { data } = await axios.get('finance/properties/city_office', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      commit('setCitiesList', data.data);
      return data;
    } catch (error) {
      return error;
    }
  },

  async downloadReport(_, { params, name }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('finance', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          ...params,
          export: 1,
        },
      });

      downloadFile(data, name);

      return { success: true };
    } catch {
      return { error: true };
    }
  },

  async fetchHistory({ commit }, params) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('finance/history', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      const history = { ...data.data.history };
      history.items = enumerateArray(
        history.items,
        history.currentPage,
        history.perPage,
      );

      commit('setHistory', history);

      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchComission({ commit }, params) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('finance/commission', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      commit('setComission', data.data.income_total_commission);
      return data;
    } catch (error) {
      return error;
    }
  },
};

const mutations = {
  setList(state, payload) {
    state.list = payload;
  },

  setConfiguratorList(state, payload) {
    state.configuratorList = payload;
  },

  setExpenseTypesList(state, payload) {
    state.expenseTypesList = payload;
  },

  setCategoriesList(state, payload) {
    state.categoriesList = payload;
  },

  setCitiesList(state, payload) {
    state.citiesList = payload;
  },

  setTotals(state, { expanseTotal }) {
    state.expanseTotal = expanseTotal;
  },

  setHistory(state, payload) {
    state.history.currentPage = payload.currentPage;
    state.history.lastPage = payload.lastPage;
    state.history.perPage = payload.perPage;
    state.history.list = payload.items;
  },

  setComission(state, payload) {
    state.comission = payload;
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
};
